import { useRequest } from 'ahooks'
import { useId, useState } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { isError } from 'lodash-es'

import { asyncSuccessResolver, toast, wait } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'

type UsePasskeyOptions = FactorHookOptions

export const usePasskey = ({
  apiInstance: { mfaChallengePasskeyOption, mfaChallengePasskeyVerify },
  onVerified
}: UsePasskeyOptions) => {
  const toastID = useId()

  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      resetActions()
    }
  }

  const doAuthenticate = async () => {
    const optionsRes = await mfaChallengePasskeyOption()
    const { data: options } = successResolver(optionsRes)

    const payloads = await startAuthentication({ optionsJSON: options })

    return { payloads, options }
  }

  const doVerify = async (res: Awaited<ReturnType<typeof doAuthenticate>>) => {
    if (!res) return

    const {
      payloads,
      options: { reference_code }
    } = res

    toast.promise(
      verifyReq.runAsync({
        reference_code,
        payloads
      }),
      {
        id: toastID,
        loading: 'Verifying passkey...',
        error: errorMessageResolver,
        success: 'Passkey verified'
      }
    )
  }

  const [authenticateError, setAuthenticateError] = useState<Error>()
  const [verifyError, setVerifyError] = useState<Error>()

  const authenticateReq = useRequest(doAuthenticate, {
    manual: true,
    onError: error => {
      setAuthenticateError(error)
      onApiError(error)
    },
    onSuccess: doVerify
  })

  const verifyReq = useRequest(
    asyncSuccessResolver(mfaChallengePasskeyVerify, successResolver),
    {
      manual: true,
      onError: error => {
        setVerifyError(error)
        onApiError(error)
      },
      onSuccess: async ({ data: { mfa_token } }) => {
        await wait(300)
        onVerified(mfa_token)
      }
    }
  )

  const requests = [authenticateReq, verifyReq]

  const hasError = [authenticateError, verifyError].some(error =>
    isError(error)
  )
  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)
  const isSuccess = !!verifyReq.data?.data.mfa_token

  const authenticate = () => {
    resetActions()
    return authenticateReq.runAsync()
  }

  const resetActions = () => {
    clearError()
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
  }

  const clearError = () => {
    setAuthenticateError(undefined)
    setVerifyError(undefined)
  }

  const reset = () => {
    resetActions()
    clearError()
    toast.dismiss(toastID)
  }

  return {
    isAnyLoading,
    reset,
    hasError,
    isSuccess,
    authenticate
  }
}

export default usePasskey
